/**
 * One-time in-app announcements rendered as a banner at the top of the app
 * shell. Reusable: to send a new message, add an entry to ANNOUNCEMENTS and
 * redeploy. Each is shown until the user dismisses it; dismissal persists
 * per-browser in localStorage, keyed by `id`.
 *
 * `audienceEmails` targets specific accounts (case-insensitive). Omit it (or
 * leave it empty) to show the announcement to everyone.
 */

export type AnnouncementKind = "info" | "warning";

export type Announcement = {
  /** Stable id; also the localStorage dismissal key. Never reuse an old id. */
  id: string;
  kind: AnnouncementKind;
  title: string;
  body: string;
  /** Emails this targets; omit/empty to show everyone. Matched case-insensitively. */
  audienceEmails?: string[];
};

export const ANNOUNCEMENTS: Announcement[] = [
  {
    id: "data-reset-2026-06",
    kind: "warning",
    title: "Your trades were reset — please re-import",
    body:
      "We found and fixed a bug where imported trades weren't linked to their import, so deleting an import didn't remove its trades. Clearing it up required removing your existing trades. Please re-import your broker statements from the Import page — new imports link correctly and can be deleted or restored.",
    audienceEmails: ["malhitrader+v1@gmail.com", "cxzmalhi@gmail.com"],
  },
];

const DISMISS_PREFIX = "announcement-dismissed:";

/** localStorage key under which a given announcement's dismissal is recorded. */
export function dismissalKey(id: string): string {
  return `${DISMISS_PREFIX}${id}`;
}

function targetsEmail(announcement: Announcement, email: string | undefined): boolean {
  const audience = announcement.audienceEmails;
  if (audience === undefined || audience.length === 0) {
    return true;
  }
  if (email === undefined) {
    return false;
  }
  const normalized = email.trim().toLowerCase();
  return audience.some((candidate) => candidate.trim().toLowerCase() === normalized);
}

/** Announcements targeting this user (audience match only; dismissal is applied by the UI). */
export function announcementsForEmail(email: string | undefined): Announcement[] {
  return ANNOUNCEMENTS.filter((announcement) => targetsEmail(announcement, email));
}
